'use client';
import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

interface ChatMessage {
  id: string;
  session_id: string;
  sender_role: 'user' | 'admin';
  content: string;
  created_at: string;
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [step, setStep] = useState<'intro' | 'chat'>('intro');
  const [sending, setSending] = useState(false);
  const [pulse, setPulse] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  useEffect(() => {
    const timer = setTimeout(() => setPulse(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!sessionId) return;
    const channel = supabase
      .channel(`chat_messages_${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMessage;
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const startChat = async () => {
    if (!visitorName.trim()) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user?.id ?? null,
          visitor_name: visitorName.trim(),
          visitor_email: visitorEmail.trim() || null,
          status: 'open',
        })
        .select('id')
        .single();
      if (error) throw error;
      setSessionId(data.id);
      setStep('chat');
      // Send welcome message from admin
      await supabase.from('chat_messages').insert({
        session_id: data.id,
        sender_role: 'admin',
        content: `Hi ${visitorName.trim()}! 👋 Welcome to DriveSA. How can we help you today?`,
      });
    } catch (err) {
      console.error('Failed to start chat session:', err);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId || sending) return;
    setSending(true);
    const content = inputValue.trim();
    setInputValue('');
    try {
      await supabase.from('chat_messages').insert({
        session_id: sessionId,
        sender_role: 'user',
        content,
      });
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === 'intro') startChat();
      else sendMessage();
    }
  };

  return (
    <>
      {/* Floating 24/7 CHAT button */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Open live chat"
        className="fixed bottom-6 left-5 z-50 flex flex-col items-center justify-center gap-0.5 group"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <div
          className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-transform duration-200 hover:scale-110 active:scale-95"
          style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)' }}
        >
          {/* Chat bubble icon */}
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
              fill="white"
            />
            <circle cx="8" cy="11" r="1.2" fill="#0f3460" />
            <circle cx="12" cy="11" r="1.2" fill="#0f3460" />
            <circle cx="16" cy="11" r="1.2" fill="#0f3460" />
          </svg>
          {/* Pulse ring */}
          {pulse && (
            <span
              className="absolute inset-0 rounded-full animate-ping opacity-40"
              style={{ background: '#0f3460' }}
              aria-hidden="true"
            />
          )}
        </div>
        <span
          className="text-white text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full shadow"
          style={{ background: 'linear-gradient(90deg, #e63946, #c1121f)', letterSpacing: '0.12em' }}
        >
          24/7 CHAT
        </span>
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-28 left-5 z-50 w-80 sm:w-96 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            maxHeight: '480px',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}
          >
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white font-semibold text-sm">DriveSA Live Chat</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          {step === 'intro' ? (
            <div className="flex flex-col gap-3 p-4">
              <p className="text-gray-600 text-sm">
                Chat with our team — we typically reply within minutes.
              </p>
              <input
                type="text"
                placeholder="Your name *"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={visitorEmail}
                onChange={(e) => setVisitorEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={startChat}
                disabled={!visitorName.trim()}
                className="w-full py-2 rounded-lg text-white text-sm font-semibold transition-opacity disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #1a1a2e, #0f3460)' }}
              >
                Start Chat
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ minHeight: 0, maxHeight: '300px' }}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                        msg.sender_role === 'user' ?'text-white rounded-br-sm' :'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                      style={
                        msg.sender_role === 'user'
                          ? { background: 'linear-gradient(135deg, #1a1a2e, #0f3460)' }
                          : {}
                      }
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-100">
                <input
                  type="text"
                  placeholder="Type a message…"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || sending}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)' }}
                  aria-label="Send message"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
